import { ErrorDeRegla } from './errores';

/**
 * LA CÉDULA
 * =========
 *
 * Es la llave con la que se busca a una persona en el mostrador, así que su forma no puede
 * ser una convención: se normaliza en un solo lugar, antes de guardar y antes de buscar.
 * Si "1.234.567" y "1234567" pudieran conviven como dos filas, ahí está el agujero por el
 * que se cobra dos veces —que es justo lo que el sistema existe para tapar—.
 *
 * Normalizar es quitar todo lo que no sea dígito: puntos, guiones, espacios. Nada más. No
 * se recorta a un largo fijo ni se completa con ceros: una cédula paraguaya vieja tiene 6
 * dígitos y una nueva 7 u 8, y "arreglarla" sería inventar un documento distinto.
 */

/** Solo los dígitos: "1.234.567" → "1234567", " 987 654 " → "987654". */
export function normalizarDocumento(valor: string): string {
  return valor.replace(/\D/g, '');
}

/**
 * Cuántos dígitos se aceptan. El mínimo de 5 deja pasar cédulas viejas y corta los tipeos
 * de dos o tres dígitos; el máximo de 12 cubre un RUC de persona jurídica.
 */
export const LARGO_MINIMO_DOCUMENTO = 5;
export const LARGO_MAXIMO_DOCUMENTO = 12;

export function esDocumentoValido(valor: string): boolean {
  const limpio = normalizarDocumento(valor);
  return limpio.length >= LARGO_MINIMO_DOCUMENTO && limpio.length <= LARGO_MAXIMO_DOCUMENTO;
}

/** Normaliza y valida de una sola pasada. Lanza con un mensaje que se le puede mostrar. */
export function asegurarDocumento(valor: string, nombre = 'La cédula'): string {
  const limpio = normalizarDocumento(valor);
  if (limpio.length === 0) throw new ErrorDeRegla(`${nombre} no puede estar vacía.`);
  if (limpio.length < LARGO_MINIMO_DOCUMENTO || limpio.length > LARGO_MAXIMO_DOCUMENTO) {
    throw new ErrorDeRegla(
      `${nombre} tiene que tener entre ${LARGO_MINIMO_DOCUMENTO} y ${LARGO_MAXIMO_DOCUMENTO} dígitos (recibí "${valor}").`,
    );
  }
  return limpio;
}

/** Cómo se lee una cédula en pantalla y en el comprobante: "1234567" → "1.234.567". */
export function formatearDocumento(documento: string): string {
  const limpio = normalizarDocumento(documento);
  return limpio.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/**
 * DÍGITO VERIFICADOR DEL RUC
 * ==========================
 *
 * El algoritmo oficial paraguayo (el mismo que usa SIFEN): se recorre la base de derecha a
 * izquierda multiplicando por 2, 3, 4… hasta 11 y volviendo a 2; se suma; el resto de
 * dividir por 11 decide. Si el resto es 0 o 1 el dígito es 0, si no es 11 menos el resto.
 *
 * Sirve para dos cosas distintas, y conviene no confundirlas:
 *   · validar el RUC que la entidad carga en su configuración (sale en el comprobante);
 *   · verificar que el `dv` que trae el listado de la DNIT corresponde a la cédula, que es
 *     la única forma de detectar una fila mal parseada en una importación de 2 millones.
 *
 * Los caracteres no numéricos (un RUC de extranjero puede traer letras) valen por su ASCII
 * menos 48, como manda la especificación.
 *
 * ES EL MISMO ALGORITMO QUE YA CORRE EN PRODUCCIÓN EN CONTAROGA
 * (`backend/src/comercial/sifen/cdc.ts → digitoVerificador`), donde calcula el DV del CDC de una
 * factura electrónica y lo valida contra SIFEN. Se reimplementó acá en vez de importarlo porque son
 * dos productos distintos, pero el ciclo de multiplicadores de 2 a 11 —que es lo que se olvida al
 * reescribirlo— está verificado contra esa implementación. La única diferencia es que ContaRoga
 * descarta los caracteres no numéricos y acá se los pesa por su ASCII, que es lo que pide la
 * especificación para los RUC con letra.
 */
export function calcularDvRuc(base: string): number {
  const limpio = base.trim().toUpperCase().replace(/[^0-9A-Z]/g, '');
  if (limpio.length === 0) throw new ErrorDeRegla('El RUC no puede estar vacío.');
  let total = 0;
  let multiplicador = 2;
  for (let i = limpio.length - 1; i >= 0; i--) {
    total += (limpio.charCodeAt(i) - 48) * multiplicador;
    multiplicador = multiplicador === 11 ? 2 : multiplicador + 1;
  }
  const resto = total % 11;
  return resto > 1 ? 11 - resto : 0;
}

/** True si "80012345-6" trae el dígito verificador que le corresponde. */
export function esRucValido(ruc: string): boolean {
  const partes = /^([0-9A-Za-z.]+)-?(\d)$/.exec(ruc.trim());
  if (!partes) return false;
  const base = partes[1]!.replace(/\./g, '');
  const dv = Number(partes[2]);
  if (base.length === 0) return false;
  return calcularDvRuc(base) === dv;
}

/** RUC completo a partir de la base: "80012345" → "80012345-6". */
export function componerRuc(base: string): string {
  const limpio = base.trim().replace(/[.\s-]/g, '');
  return `${limpio}-${calcularDvRuc(limpio)}`;
}
